<template>
    <form @submit.prevent="$emit('submit')" class="space-y-6">
        <FormSection>
            <template #title>
                Course Information
            </template>

            <FormGroup
                name="title"
                label="Title">
                <Input
                    ref="title"
                    v-model="form.title"
                    name="title"
                    placeholder="Please enter the title..."/>
            </FormGroup>

            <FormGroup
                name="summary"
                label="Summary">
                <Textarea
                    v-model="form.summary"
                    name="summary"
                    rows="4"
                    placeholder="Please enter the summary..."/>
            </FormGroup>

            <FormGroup
                name="description"
                label="Description">
                <Textarea
                    v-model="form.description"
                    name="description"
                    rows="6"
                    placeholder="Please enter the description..."/>
            </FormGroup>

            <div class="row">
                <div class="col-full lg:col-1/3">
                    <FormGroup
                        name="type"
                        label="Type">
                        <Select
                            v-model="form.type"
                            name="type"
                            :options="course_types"
                            placeholder="Select a course type..."/>
                    </FormGroup>
                </div>
                <div class="col-full lg:col-1/3">
                    <FormGroup
                        name="pathway_id"
                        label="Pathway">
                        <Select
                            v-model="form.pathway_id"
                            name="pathway_id"
                            :options="pathways"
                            placeholder="Select a pathway..."/>
                    </FormGroup>
                </div>
                <div class="col-full lg:col-1/3">
                    <FormGroup
                        name="credits"
                        label="Credits">
                        <Input
                            v-model="form.credits"
                            name="credits"
                            class="max-w-28"
                            placeholder="e.g. 30"/>
                    </FormGroup>
                </div>
            </div>
        </FormSection>

        <ModulesFormSection :course="course"/>

        <FormSection>
            <template #title>
                Images
            </template>

            <FormGroup
                name="main_image_id"
                label="Main Image">
                <File
                    v-model="form.main_image_id"
                    :file="mainImage"
                    :image="true"
                    accept="image/jpeg,image/png"/>
            </FormGroup>

            <FormGroup
                name="thumbnail_image_id"
                label="Thumbnail Image">
                <File
                    v-model="form.thumbnail_image_id"
                    :file="thumbnailImage"
                    :image="true"
                    accept="image/jpeg,image/png"/>
            </FormGroup>

        </FormSection>

        <FormSection>

            <template #title>
                Trainers
            </template>

            <ul v-if="trainers.length" class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                <li v-for="(trainer, i) in trainers">
                    <FormGroup
                        :name="`trainer_ids.${i}`"
                        :label="trainer.label"
                        inline>
                        <Checkbox
                            v-model="form.trainer_ids"
                            :value="trainer.value"/>
                    </FormGroup>
                </li>
            </ul>
            <div v-else class="bg-blue-100 border-t-4 border-blue-500 rounded-b text-blue-900 px-4 py-3 shadow-md"
                 role="alert">
                <div class="flex">
                    <div>
                        <svg class="fill-current h-6 w-6 text-blue-500 mr-4" viewBox="0 0 20 20">
                            <path
                                d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/>
                        </svg>
                    </div>
                    <div>
                        <p>The current aren't any trainers in the system.</p>
                    </div>
                </div>
            </div>

            <InputError
                v-if="form.hasError('trainer_ids')"
                :data-name="name"
                :message="form.getError('trainer_ids')"/>

        </FormSection>

        <FormSection>

            <template #title>
                Moderators
            </template>

            <ul v-if="moderators.length" class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                <li v-for="(moderator, i) in moderators">
                    <FormGroup
                        :name="`moderator_ids.${i}`"
                        :label="moderator.label"
                        inline>
                        <Checkbox
                            v-model="form.moderator_ids"
                            :value="moderator.value"/>
                    </FormGroup>
                </li>
            </ul>
            <div v-else class="bg-blue-100 border-t-4 border-blue-500 rounded-b text-blue-900 px-4 py-3 shadow-md"
                 role="alert">
                <div class="flex">
                    <div>
                        <svg class="fill-current h-6 w-6 text-blue-500 mr-4" viewBox="0 0 20 20">
                            <path
                                d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/>
                        </svg>
                    </div>
                    <div>
                        <p>The current aren't any moderators in the system.</p>
                    </div>
                </div>
            </div>

            <InputError
                v-if="form.hasError('moderator_ids')"
                :data-name="name"
                :message="form.getError('moderator_ids')"/>

            <template v-if="$slots.footer" #footer>
                <slot name="footer"></slot>
            </template>
        </FormSection>
    </form>
</template>

<script>

import FormSection from "@/Components/Form/FormSection.vue";
import FormGroup from "@/Components/Form/FormGroup.vue";
import Input from "@/Components/Form/Input.vue";
import Select from "@/Components/Form/Select.vue";
import File from "@/Components/Form/File.vue";
import Textarea from "@/Components/Form/Textarea.vue";
import InputError from "@/Components/Form/InputError.vue";
import Label from "@/Components/Form/Label.vue";
import Checkbox from "@/Components/Form/Checkbox.vue";
import {optionsToArray} from "@/Lib/Helpers";
import axios from "axios";
import ModulesFormSection from "@/Pages/Admin/Courses/Components/ModulesFormSection";

export default {

    name: "CourseForm",

    props: {
        course: {
            type: Object,
            default: () => ({})
        },
        mainImage: {
            type: Object,
            default: () => ({})
        },
        thumbnailImage: {
            type: Object,
            default: () => ({})
        }
    },

    emits: ['submit'],

    data() {
        return {
            loaded: false,
            pathways: [],
            trainers: [],
            moderators: [],
            types: [
                {label: "Zoom", value: "Zoom"},
                {label: "Face-to-face", value: "F2F"},
            ],
            course_types: [
                {label: "Educator Led", value: 1},
                {label: "E-Learning", value: 2},
            ]
        };
    },

    inject: ['form'],

    async created() {
        await Promise.all([
            this.fetchPathways(),
            this.fetchTrainers(),
            this.fetchModerators(),
        ])

        this.loaded = true;
    },

    components: {
        FormSection,
        FormGroup,
        Input,
        Select,
        File,
        Textarea,
        Label,
        InputError,
        Checkbox,
        ModulesFormSection
    },

    methods: {
        async fetchPathways() {
            this.pathways = optionsToArray((await axios.get(route('api.pathways.index') + '?list')).data);
        },
        async fetchTrainers() {
            this.trainers = optionsToArray((await axios.get(route('api.users.index') + '?list&role=trainer')).data);
        },
        async fetchModerators() {
            this.moderators = optionsToArray((await axios.get(route('api.users.index') + '?list&role=moderator')).data);
        }
    },

    watch: {
        'form.trainer_ids'() {
            this.form.errors.clear('trainer_ids');
        }
    }
};

</script>
